// src/components/BulkImport/BulkImport.js
/**
 * Bulk Import Component
 * =====================
 *
 * Modulo de importacao em massa de leads via Excel.
 *
 * Fluxo:
 * 1. Upload do arquivo Excel
 * 2. Mapeamento de colunas
 * 3. Confirmacao e inicio da importacao
 * 4. Acompanhamento do progresso
 *
 * A importacao eh processada em background via Celery.
 * Toda criacao de Lead utiliza a API existente do CRM.
 */

import React, { useState, useEffect, useCallback } from "react";
import { api } from "../../services/api";
import { toast } from "react-toastify";
import {
  FaUpload,
  FaCloudUploadAlt,
  FaFileExcel,
  FaFileImport,
  FaArrowRight,
  FaArrowLeft,
  FaPlay,
  FaCheck,
  FaTimes,
  FaSpinner,
  FaHistory,
  FaExclamationTriangle,
} from "react-icons/fa";
import "./BulkImport.css";

const BulkImport = () => {
  // Estados principais
  const [step, setStep] = useState(1); // 1: Upload, 2: Mapeamento, 3: Progresso
  const [loading, setLoading] = useState(false);

  // Dados do upload
  const [uploadData, setUploadData] = useState(null);

  // Campos da API e stages
  const [apiFields, setApiFields] = useState([]);
  const [stages, setStages] = useState([]);
  const [selectedStageId, setSelectedStageId] = useState("");

  // Mapeamento de colunas
  const [columnMapping, setColumnMapping] = useState({});

  // Valores padrao para campos obrigatorios nao mapeados
  const [defaultValues, setDefaultValues] = useState({});

  // Job de importacao
  const [currentJob, setCurrentJob] = useState(null);
  const [jobs, setJobs] = useState([]);

  // Polling do progresso
  const [polling, setPolling] = useState(false);

  // Carrega campos da API e stages ao montar
  useEffect(() => {
    loadApiFields();
    loadStages();
    loadJobs();
  }, []);

  const loadApiFields = async () => {
    try {
      const response = await api.get("/api/bulk-import/fields");
      setApiFields(response.data.fields);
    } catch (error) {
      console.error("Erro ao carregar campos:", error);
      toast.error("Erro ao carregar campos da API");
    }
  };

  const loadStages = async () => {
    try {
      const response = await api.get("/api/bulk-import/stages");
      setStages(response.data.stages);
      if (response.data.stages.length > 0) {
        setSelectedStageId(response.data.stages[0].id);
      }
    } catch (error) {
      console.error("Erro ao carregar stages:", error);
      toast.error("Erro ao carregar etapas do CRM");
    }
  };

  const loadJobs = async () => {
    try {
      const response = await api.get("/api/bulk-import/jobs?limit=10");
      setJobs(response.data.jobs);
    } catch (error) {
      console.error("Erro ao carregar historico:", error);
    }
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Valida extensao
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Apenas arquivos .xlsx sao permitidos");
      return;
    }

    // Valida tamanho (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Arquivo muito grande. Maximo: 10MB");
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await api.post("/api/bulk-import/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setUploadData(response.data);

      // Inicializa mapeamento automatico
      autoMapColumns(response.data.headers);

      toast.success("Arquivo carregado com sucesso!");
      setStep(2);
    } catch (error) {
      console.error("Erro no upload:", error);
      toast.error(error.response?.data?.error || "Erro ao fazer upload");
    } finally {
      setLoading(false);
    }
  };

  // Mapeamento automatico de colunas
  const autoMapColumns = (headers) => {
    const mapping = {};
    const fieldMap = {
      // Mapeamentos comuns
      nome: "name",
      name: "name",
      email: "email",
      "e-mail": "email",
      telefone: "phone",
      phone: "phone",
      celular: "phone",
      empresa: "company",
      company: "company",
      descricao: "description",
      description: "description",
      observacao: "description",
      observacoes: "description",
      valor: "deal_value",
      cnpj: "custom_fields.cnpj",
      cidade: "custom_fields.cidade",
      estado: "custom_fields.estado",
      uf: "custom_fields.estado",
      endereco: "custom_fields.endereco",
      cargo: "custom_fields.cargo",
      prioridade: "priority",
      tags: "tags",
    };

    headers.forEach((header) => {
      const headerLower = header.toLowerCase().trim();
      if (fieldMap[headerLower]) {
        mapping[header] = fieldMap[headerLower];
      }
    });

    setColumnMapping(mapping);
  };

  // Atualiza mapeamento de uma coluna
  const handleMappingChange = (excelColumn, apiField) => {
    setColumnMapping((prev) => ({
      ...prev,
      [excelColumn]: apiField || undefined,
    }));
  };

  // Verifica se campo obrigatorio esta mapeado ou tem valor padrao
  const isRequiredFieldSatisfied = (fieldName) => {
    return Object.values(columnMapping).includes(fieldName) ||
           (defaultValues[fieldName] && defaultValues[fieldName].trim() !== "");
  };

  // Atualiza valor padrao de um campo
  const handleDefaultValueChange = (fieldName, value) => {
    setDefaultValues((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  // Inicia importacao
  const startImport = async () => {
    // Valida se o campo name esta mapeado OU tem valor padrao
    if (!isRequiredFieldSatisfied("name")) {
      toast.error('O campo "Nome" deve ser mapeado ou ter um valor padrao');
      return;
    }

    setLoading(true);

    // Filtra apenas valores padrao preenchidos
    const filledDefaultValues = {};
    Object.entries(defaultValues).forEach(([field, value]) => {
      if (value && value.trim() !== "") {
        filledDefaultValues[field] = value.trim();
      }
    });

    try {
      const response = await api.post("/api/bulk-import/start", {
        file_path: uploadData.file_path,
        original_filename: uploadData.original_filename,
        column_mapping: columnMapping,
        stage_id: selectedStageId,
        total_rows: uploadData.total_rows,
        default_values: filledDefaultValues,
      });

      setCurrentJob({
        id: response.data.job_id,
        status: "PENDING",
        total_rows: uploadData.total_rows,
        processed_rows: 0,
        success_count: 0,
        error_count: 0,
        duplicate_count: 0,
        progress_percent: 0,
      });

      toast.success("Importacao iniciada!");
      setStep(3);
      startPolling(response.data.job_id);
    } catch (error) {
      console.error("Erro ao iniciar importacao:", error);
      toast.error(error.response?.data?.error || "Erro ao iniciar importacao");
    } finally {
      setLoading(false);
    }
  };

  // Polling do progresso
  const startPolling = useCallback((jobId) => {
    setPolling(true);

    const pollInterval = setInterval(async () => {
      try {
        const response = await api.get(`/api/bulk-import/jobs/${jobId}`);
        const job = response.data;
        setCurrentJob(job);

        if (job.status === "COMPLETED" || job.status === "FAILED" || job.status === "CANCELLED") {
          clearInterval(pollInterval);
          setPolling(false);
          loadJobs(); // Atualiza historico

          if (job.status === "COMPLETED") {
            toast.success(
              `Importacao concluida! ${job.success_count} leads criados.`
            );
          } else if (job.status === "FAILED") {
            toast.error("Importacao falhou. Verifique os detalhes.");
          }
        }
      } catch (error) {
        console.error("Erro no polling:", error);
      }
    }, 2000); // Poll a cada 2 segundos

    return () => clearInterval(pollInterval);
  }, []);

  // Reset para nova importacao
  const resetImport = () => {
    setStep(1);
    setUploadData(null);
    setColumnMapping({});
    setDefaultValues({});
    setCurrentJob(null);
    loadJobs();
  };

  // Render Step 1: Upload
  const renderUploadStep = () => (
    <div className="bulk-import-step upload-step">
      <div className="upload-zone">
        <input
          type="file"
          id="file-upload"
          accept=".xlsx"
          onChange={handleFileUpload}
          disabled={loading}
        />
        <label htmlFor="file-upload" className={`upload-label ${loading ? "disabled" : ""}`}>
          {loading ? (
            <>
              <FaSpinner className="icon spin" />
              <span>Carregando arquivo...</span>
            </>
          ) : (
            <>
              <FaCloudUploadAlt className="icon" />
              <span>Arraste um arquivo Excel ou clique para selecionar</span>
              <small>Apenas arquivos .xlsx (max 10MB)</small>
            </>
          )}
        </label>
      </div>

      {/* Historico de importacoes */}
      {jobs.length > 0 && (
        <div className="import-history">
          <h3>
            <FaHistory /> Historico de Importacoes
          </h3>
          <div className="history-list">
            {jobs.slice(0, 5).map((job) => (
              <div key={job.id} className={`history-item status-${job.status.toLowerCase()}`}>
                <div className="history-info">
                  <FaFileExcel />
                  <span className="filename">{job.original_filename}</span>
                </div>
                <div className="history-stats">
                  <span className="stat success">{job.success_count} criados</span>
                  {job.error_count > 0 && (
                    <span className="stat error">{job.error_count} erros</span>
                  )}
                  {job.duplicate_count > 0 && (
                    <span className="stat duplicate">{job.duplicate_count} duplicados</span>
                  )}
                </div>
                <span className={`status-badge ${job.status.toLowerCase()}`}>
                  {job.status === "COMPLETED" ? "Concluido" :
                   job.status === "PROCESSING" ? "Processando" :
                   job.status === "PENDING" ? "Pendente" :
                   job.status === "FAILED" ? "Falhou" : job.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Render Step 2: Mapeamento
  const renderMappingStep = () => (
    <div className="bulk-import-step mapping-step">
      <div className="mapping-header">
        <h3>
          <FaFileExcel /> {uploadData?.original_filename}
        </h3>
        <p>{uploadData?.total_rows} registros encontrados</p>
      </div>

      {/* Preview da planilha */}
      <div className="preview-section">
        <h4>Preview dos dados</h4>
        <div className="preview-table-container">
          <table className="preview-table">
            <thead>
              <tr>
                {uploadData?.headers?.map((header, idx) => (
                  <th key={idx}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {uploadData?.preview_data?.map((row, rowIdx) => (
                <tr key={rowIdx}>
                  {uploadData?.headers?.map((header, colIdx) => (
                    <td key={colIdx}>{row[header] || "-"}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Selecao de Stage */}
      <div className="stage-selection">
        <label>Etapa do CRM para os novos leads:</label>
        <select
          value={selectedStageId}
          onChange={(e) => setSelectedStageId(e.target.value)}
        >
          {stages.map((stage) => (
            <option key={stage.id} value={stage.id}>
              {stage.name}
            </option>
          ))}
        </select>
      </div>

      {/* Mapeamento de colunas */}
      <div className="mapping-section">
        <h4>Mapeamento de Colunas</h4>
        <p className="mapping-hint">
          Associe cada coluna da planilha ao campo correspondente no CRM.
        </p>

        <div className="mapping-grid">
          {uploadData?.headers?.map((header) => (
            <div key={header} className="mapping-row">
              <div className="excel-column">
                <FaFileExcel />
                <span>{header}</span>
              </div>
              <FaArrowRight className="arrow" />
              <select
                value={columnMapping[header] || ""}
                onChange={(e) => handleMappingChange(header, e.target.value)}
                className={columnMapping[header] === "name" ? "mapped-required" : ""}
              >
                <option value="">-- Ignorar --</option>
                {apiFields.map((field) => (
                  <option key={field.field} value={field.field}>
                    {field.label} {field.required ? "*" : ""}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>

        {/* Valor padrao para Nome - aparece se nome nao mapeado ou sempre como fallback */}
        <div className="name-fallback-section">
          <div className="name-fallback-row">
            <label>
              <span className="fallback-label">Valor padrao para Nome</span>
              <span className="fallback-hint">
                (usado quando a celula estiver vazia)
              </span>
            </label>
            <input
              type="text"
              placeholder="Ex: Lead Importado"
              value={defaultValues.name || ""}
              onChange={(e) => handleDefaultValueChange("name", e.target.value)}
              className={!Object.values(columnMapping).includes("name") && !defaultValues.name ? "needs-value" : ""}
            />
          </div>
          {!Object.values(columnMapping).includes("name") && !defaultValues.name && (
            <small className="fallback-warning">
              <FaExclamationTriangle /> Campo "Nome" nao mapeado. Defina um valor padrao.
            </small>
          )}
        </div>
      </div>

      {/* Botoes de acao */}
      <div className="mapping-actions">
        <button className="btn-secondary" onClick={() => setStep(1)}>
          <FaArrowLeft /> Voltar
        </button>
        <button
          className="btn-primary"
          onClick={startImport}
          disabled={loading || !isRequiredFieldSatisfied("name")}
        >
          {loading ? (
            <>
              <FaSpinner className="spin" /> Iniciando...
            </>
          ) : (
            <>
              <FaPlay /> Iniciar Importacao
            </>
          )}
        </button>
      </div>
    </div>
  );

  // Render Step 3: Progresso
  const renderProgressStep = () => (
    <div className="bulk-import-step progress-step">
      <div className="progress-header">
        <h3>
          {currentJob?.status === "COMPLETED" ? (
            <FaCheck className="icon success" />
          ) : currentJob?.status === "FAILED" ? (
            <FaTimes className="icon error" />
          ) : (
            <FaSpinner className="icon spin" />
          )}
          {currentJob?.status === "COMPLETED"
            ? "Importacao Concluida"
            : currentJob?.status === "FAILED"
            ? "Importacao Falhou"
            : "Importando Leads..."}
        </h3>
      </div>

      {/* Barra de progresso */}
      <div className="progress-container">
        <div className="progress-bar">
          <div
            className={`progress-fill ${currentJob?.status?.toLowerCase()}`}
            style={{ width: `${currentJob?.progress_percent || 0}%` }}
          />
        </div>
        <div className="progress-text">
          {currentJob?.processed_rows || 0} / {currentJob?.total_rows || 0} registros
          ({currentJob?.progress_percent || 0}%)
        </div>
      </div>

      {/* Estatisticas */}
      <div className="progress-stats">
        <div className="stat-card success">
          <FaCheck />
          <div className="stat-value">{currentJob?.success_count || 0}</div>
          <div className="stat-label">Criados</div>
        </div>
        <div className="stat-card error">
          <FaTimes />
          <div className="stat-value">{currentJob?.error_count || 0}</div>
          <div className="stat-label">Erros</div>
        </div>
        <div className="stat-card duplicate">
          <FaExclamationTriangle />
          <div className="stat-value">{currentJob?.duplicate_count || 0}</div>
          <div className="stat-label">Duplicados</div>
        </div>
      </div>

      {/* Detalhes de erros */}
      {currentJob?.error_details?.length > 0 && (
        <div className="error-details">
          <h4>
            <FaExclamationTriangle /> Detalhes dos Erros
          </h4>
          <div className="error-list">
            {currentJob.error_details.slice(0, 10).map((err, idx) => (
              <div key={idx} className="error-item">
                <span className="error-row">Linha {err.row}:</span>
                <span className="error-msg">{err.error}</span>
              </div>
            ))}
            {currentJob.error_details.length > 10 && (
              <div className="error-more">
                ... e mais {currentJob.error_details.length - 10} erros
              </div>
            )}
          </div>
        </div>
      )}

      {/* Botao de nova importacao */}
      {(currentJob?.status === "COMPLETED" || currentJob?.status === "FAILED") && (
        <div className="progress-actions">
          <button className="btn-primary" onClick={resetImport}>
            <FaUpload /> Nova Importacao
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="bulk-import-container">
      <div className="bulk-import-header">
        <h1>
          <FaFileImport /> Importacao em Massa
        </h1>
        <p>Importe leads de planilhas Excel para o CRM</p>
      </div>

      {/* Stepper */}
      <div className="stepper">
        <div className={`step ${step >= 1 ? "active" : ""} ${step > 1 ? "completed" : ""}`}>
          <div className="step-number">1</div>
          <span>Upload</span>
        </div>
        <div className={`step ${step >= 2 ? "active" : ""} ${step > 2 ? "completed" : ""}`}>
          <div className="step-number">2</div>
          <span>Mapeamento</span>
        </div>
        <div className={`step ${step >= 3 ? "active" : ""}`}>
          <div className="step-number">3</div>
          <span>Importacao</span>
        </div>
      </div>

      {/* Conteudo do step atual */}
      <div className="bulk-import-content">
        {step === 1 && renderUploadStep()}
        {step === 2 && renderMappingStep()}
        {step === 3 && renderProgressStep()}
      </div>
    </div>
  );
};

export default BulkImport;
